import type { User, Journal, Entry, Submission, LoginResponse, JournalStats, SystemStats, RubricGrid } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api';


class ApiServiceClass {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(response.access_token);
    return response;
  }

  async register(email: string, name: string, password: string, role: string = 'student'): Promise<{ user_id: string }> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password, role }),
    });
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/auth/me');
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout', { method: 'POST' }).catch(() => {});
    this.setToken(null);
  }

  getMoodleLoginUrl(): string {
    return `${API_URL}/auth/moodle/login`;
  }

  // Journals
  async getJournals(): Promise<Journal[]> {
    return this.request<Journal[]>('/journals');
  }

  async getJournal(journalId: string): Promise<Journal> {
    return this.request<Journal>(`/journals/${journalId}`);
  }

  async createJournal(data: { title: string; description?: string }): Promise<{ id: string }> {
    return this.request('/journals', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateJournal(journalId: string, data: { title?: string; description?: string }): Promise<{ message: string }> {
    return this.request(`/journals/${journalId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteJournal(journalId: string): Promise<{ message: string }> {
    return this.request(`/journals/${journalId}`, { method: 'DELETE' });
  }

  async getJournalStats(journalId: string): Promise<JournalStats> {
    return this.request<JournalStats>(`/journals/${journalId}/stats`);
  }

  async enrollStudent(journalId: string, studentId: string): Promise<void> {
    await this.request(`/journals/${journalId}/enroll`, {
      method: 'POST',
      body: JSON.stringify({ student_id: studentId }),
    });
  }

  async getJournalStudents(journalId: string): Promise<User[]> {
    return this.request<User[]>(`/journals/${journalId}/students`);
  }

  // Alias for backward compatibility
  async getEnrolledStudents(journalId: string): Promise<User[]> {
    return this.getJournalStudents(journalId);
  }

  // Alias for getEntrySubmissions
  async getSubmissions(entryId: string): Promise<Submission[]> {
    return this.getEntrySubmissions(entryId);
  }

  // Entries
  async getEntries(journalId: string): Promise<Entry[]> {
    return this.request<Entry[]>(`/journals/${journalId}/entries`);
  }

  async createEntry(journalId: string, data: {
    title: string;
    instructions: string;
    rubric: RubricGrid;
    due_date?: string;
    min_words?: number;
    max_words?: number;
    max_attempts?: number;
    max_score?: number;
    allow_resubmission?: boolean;
  }): Promise<{ id: string }> {
    return this.request(`/journals/${journalId}/entries`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateEntry(_journalId: string, entryId: string, data: Partial<Entry>): Promise<void> {
    await this.request(`/entries/${entryId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteEntry(entryId: string): Promise<void> {
    await this.request(`/entries/${entryId}`, { method: 'DELETE' });
  }

  async toggleEntryVisibility(entryId: string): Promise<{ is_visible: boolean }> {
    return this.request(`/entries/${entryId}/toggle-visibility`, { method: 'POST' });
  }

  // Submissions
  async submitReflection(entryId: string, contentHtml: string): Promise<{ id: string }> {
    return this.request(`/entries/${entryId}/submit`, {
      method: 'POST',
      body: JSON.stringify({ content_html: contentHtml }),
    });
  }

  async getEntrySubmissions(entryId: string): Promise<Submission[]> {
    return this.request<Submission[]>(`/entries/${entryId}/submissions`);
  }

  async getSubmission(submissionId: string): Promise<{ submission: Submission; feedback: any; entry: Entry }> {
    return this.request(`/submissions/${submissionId}`);
  }

  async gradeSubmission(submissionId: string, grade: number, comments: string): Promise<void> {
    await this.request(`/submissions/${submissionId}/grade`, {
      method: 'PUT',
      body: JSON.stringify({ grade, comments }),
    });
  }

  async regenerateAIEvaluation(submissionId: string): Promise<{ message: string; status: string }> {
    return this.request(`/submissions/${submissionId}/regenerate-ai`, {
      method: 'POST',
    });
  }

  async updateAIFeedback(submissionId: string, data: {
    overall_feedback?: string;
    total_score?: number;
    strengths?: string[];
    growth_opportunities?: string[];
  }): Promise<{ message: string; ai_feedback: any }> {
    return this.request(`/submissions/${submissionId}/ai-feedback`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Reports
  async generateReport(journalId: string): Promise<Blob> {
    const response = await fetch(`${API_URL}/journals/${journalId}/reports/generate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type: 'csv' }),
    });
    return response.blob();
  }

  getReportDownloadUrl(journalId: string): string {
    return `${API_URL}/journals/${journalId}/reports/generate?token=${this.token}`;
  }

  // Admin
  async getUsers(role?: string): Promise<User[]> {
    const query = role ? `?role=${role}` : '';
    return this.request<User[]>(`/admin/users${query}`);
  }

  async createUser(data: {
    email: string;
    name: string;
    password: string;
    role: string;
    usn?: string;
    program?: string;
    cohort?: string;
  }): Promise<{ id: string }> {
    return this.request('/admin/users', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateUser(userId: string, data: {
    name?: string;
    email?: string;
    role?: string;
    usn?: string;
    program?: string;
    cohort?: string;
  }): Promise<{ message: string }> {
    return this.request(`/admin/users/${userId}`, { 
      method: 'PUT', 
      body: JSON.stringify(data) 
    });
  }

  async toggleUserStatus(userId: string): Promise<{ disabled: boolean }> {
    return this.request(`/admin/users/${userId}/toggle-status`, { method: 'POST' });
  }

  async getAuditLogs(params?: { limit?: number; offset?: number }): Promise<{ logs: any[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const query = searchParams.toString();
    return this.request(`/admin/audit-logs${query ? `?${query}` : ''}`);
  }

  async getSystemStats(): Promise<SystemStats> {
    return this.request<SystemStats>('/admin/system-stats');
  }

  async healthCheck(): Promise<{ status: string; database: string }> {
    return this.request('/health');
  }
}

export const ApiService = new ApiServiceClass();